import { AsyncPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Params, RouterLink } from '@angular/router';
import { WINDOW } from '@ng-web-apis/common';
import { I18NextModule } from 'angular-i18next';
import { combineLatest, map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { User, UserRole } from 'src/types/graphql';
import { AvatarDirective } from '../../../common/avatar/avatar.directive';
import { BaseComponent } from '../base/base.component';
import { IconComponent } from '../../../common/icon/icon.component';
import { RouteTranslateService } from '../../../common/route-translate/route-translate.service';
import { UserService } from '../../../common/user/user.service';
import { RouteTranslatePipe } from '../../pipes/route-translate.pipe';
import { SearchResultsFilterablesService } from '../../../../../utils/search-results-filterable';
import { ParamsService } from '../../../pages/search-results/params/params.service';

export interface ListItem {
  class: string;
  icon?: string;
  type?: 'phosphor' | 'custom';
  text: string;
  avatar: boolean;
  title?: string;
  image?: string;
  notifications: boolean;
  route?: string | any[];
  routeParams?: Params;
  pinValue$?: Observable<number>;
  active?: boolean;
  handler?: () => void;
}

@Component({
  selector: 'sharry-header-menu',
  standalone: true,
  imports: [
    I18NextModule,
    IconComponent,
    AvatarDirective,
    AsyncPipe,
    NgClass,
    NgStyle,
    RouterLink,
    RouteTranslatePipe,
    MatButtonModule,
    NgTemplateOutlet,
  ],
  templateUrl: './header-menu.component.html',
  styleUrl: './header-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderMenuComponent extends BaseComponent {
  menuOpened = false;
  userList$: Observable<{
    user: Partial<User>;
    list: ListItem[][];
  }>;
  totalUnread$: Observable<number>;
  @Output() expandedMenu = new EventEmitter<boolean>();
  env: 'production' | 'staging' | 'development' = environment.env as 'production' | 'staging' | 'development';

  constructor(
    private cd: ChangeDetectorRef,
    public userService: UserService,
    @Inject(WINDOW) private window: Window,
    private routeTranslateService: RouteTranslateService,
    private searchResultsFilterablesService: SearchResultsFilterablesService,
    private paramService: ParamsService,
  ) {
    super();
    this.totalUnread$ = combineLatest([this.userService.unreadMessages$, this.userService.unreadNotifications$]).pipe(
      map(([message, notification]) => message + notification),
    );
    this.userList$ = this.userService.user$.pipe(
      map(user => ({
        user: user,
        list: this.listsHandler(user),
      })),
    );
  }

  toggleMenu(item?: ListItem) {
    this.menuOpened = !this.menuOpened;
    this.expandedMenu.emit(this.menuOpened);
    if (item?.text === 'header.shop') {
      this.paramService.setKindsFromUrlTypes(this.setQueryParams());
    }
    this.cd.detectChanges();
  }

  listsHandler(user: Partial<User>) {
    const list: ListItem[][] = [
      [
        {
          class: 'main-routes',
          image: 'https://media.sharry.land/app/images/menu/explore.png',
          avatar: false,
          notifications: false,
          text: 'header.explore',
          route: 'explore',
          active: this.window?.location?.pathname === this.routeTranslateService.translateRoute('explore'),
        },
        {
          class: 'main-routes',
          icon: 'preferiti',
          avatar: false,
          notifications: false,
          text: 'header.likes-short',
          route: 'likes/default',
          active: this.window?.location?.pathname === this.routeTranslateService.translateRoute('likes/default'),
        },
        {
          class: 'main-routes',
          icon: 'ico-community-sfumata',
          avatar: false,
          notifications: false,
          text: 'header.community',
          route: 'community',
          active: this.window?.location?.pathname === this.routeTranslateService.translateRoute('community'),
        },
        {
          class: 'main-routes',
          icon: 'zaino-desktop',
          avatar: false,
          notifications: false,
          text: 'header.shop',
          route: 'search-results',
          routeParams: { types: this.setQueryParams() },
          active: this.window?.location?.pathname === this.routeTranslateService.translateRoute('search-results'),
        },
      ],
    ];
    if (user) {
      list[0].push({
        class: 'main-routes',
        avatar: true,
        notifications: false,
        text: 'header.home-profile',
        route: 'profile/home',
        active: this.window?.location?.pathname === this.routeTranslateService.translateRoute('profile/home'),
      });
      list.push(
        [
          {
            class: 'secondary-routes',
            icon: 'chat-circle-text',
            avatar: false,
            notifications: false,
            text: 'header.messages',
            route: 'messages',
            pinValue$: this.userService.unreadMessages$,
          },
          {
            class: 'secondary-routes',
            icon: 'bell',
            avatar: false,
            notifications: false,
            text: 'header.notifications',
            route: 'notifications',
            pinValue$: this.userService.unreadNotifications$,
          },
        ],
        [
          user.isSmallOrganisation
            ? {
                class: 'secondary-routes',
                icon: 'worker',
                type: 'custom',
                avatar: false,
                notifications: false,
                text: 'header.pro-activity',
                route: 'dashboard/sharry-management',
                pinValue$: this.userService.unreadNotificationsDashboard$,
              }
            : null,
          {
            class: 'secondary-routes',
            type: 'custom',
            icon: 'i-miei-acquisti',
            avatar: false,
            notifications: false,
            text: 'header.bookings',
            route: 'sharry-activity/bookings',
            pinValue$: this.userService.unreadNotificationsBookings$,
          },
          {
            class: 'secondary-routes',
            icon: 'cherrymeter',
            type: 'custom',
            avatar: false,
            notifications: false,
            text: 'header.cherrymeter',
            route: 'cherry-meter',
          },
          /*          {
            class: 'secondary-routes',
            icon: 'gear',
            avatar: false,
            notifications: false,
            text: 'header.settings',
            route: 'profile/home',
            routeParams: { i: 0 },
          },*/
          {
            class: 'secondary-routes',
            icon: 'gift',
            avatar: false,
            notifications: false,
            text: 'header.gift-card',
            route: 'gift-card',
          },
        ],
        [
          {
            class: 'secondary-routes',
            icon: 'sign-out',
            avatar: false,
            notifications: false,
            text: 'header.sign-out',
            route: [],
            routeParams: { signout: true },
          },
        ],
      );
    } else {
      list.unshift([
        {
          class: 'auth-routes',
          avatar: false,
          notifications: false,
          text: 'header.sign-up',
          route: [],
          routeParams: { signup: true },
        },
        {
          class: 'auth-routes',
          avatar: false,
          notifications: false,
          text: 'header.sign-in',
          route: [],
          routeParams: { signin: true },
        },
      ]);
      list.push(
        [
          {
            class: 'secondary-routes',
            icon: 'map-trifold',
            avatar: false,
            notifications: false,
            text: 'header.map-for-you',
            route: 'wonders-map',
          },
          {
            class: 'secondary-routes',
            icon: 'cherrymeter',
            type: 'custom',
            avatar: false,
            notifications: false,
            text: 'header.cherrymeter',
            route: 'cherry-meter',
          },
          {
            class: 'secondary-routes',
            icon: 'gift',
            avatar: false,
            notifications: false,
            text: 'header.gift-card',
            route: 'gift-card',
          },
        ],
        [
          {
            class: 'secondary-routes',
            icon: 'worker',
            type: 'custom',
            avatar: false,
            notifications: false,
            title: 'homepage.pro.title',
            text: 'header.become-pro-map',
            route: 'become-pro',
          },
        ],
      );
    }
    if (user && user.role !== UserRole.StandardUser && user.role !== UserRole.TrustedUser) {
      list[1].unshift({
        class: 'secondary-routes',
        icon: 'chart-bar',
        avatar: false,
        notifications: false,
        text: 'Admin',
        handler: () => this.adminHandler(),
      });
    }
    return list.map(l => l.filter(subl => subl));
  }

  adminHandler() {
    let path: string;
    switch (this.userService.user?.role) {
      case UserRole.Admin:
        path = 'profile-contents/users';
        break;
      case UserRole.Validator:
        path = 'contents/sharry';
        break;
      case UserRole.EventValidator:
        path = 'contents/events';
        break;
      default:
        path = '';
        throw 'UNAUTHORIZED';
    }
    this.window.open(
      `https://backoffice.sharry.land/${environment.env === 'production' ? 'production' : 'staging'}/${path}`,
      '_blank',
    );
  }

  setQueryParams() {
    return [
      ...this.searchResultsFilterablesService.experienceFilterableIds,
      ...this.searchResultsFilterablesService.tourFilterableIds,
      ...this.searchResultsFilterablesService.itemFilterableIds,
    ];
  }
}
